/**
 * useCapability — read a single capability slot from the manifest.
 *
 * Returns the typed diff plus a stable ``state`` discriminant callers can
 * switch on. While the manifest is still loading, ``state === "loading"``
 * and ``diff === null`` so consumers render a skeleton rather than nag the
 * user about a slot they may already have.
 *
 * ``useHasCapability(name)`` is the boolean fast-path — returns ``true`` only
 * when the capability is ``satisfied``. During loading it returns ``false``
 * so feature gates default to opt-out, never accidentally exposing a tool
 * that wasn't granted yet.
 *
 * Ports v1 ``ando-frontend/app/hooks/useCapability.ts``.
 */

import type { CapabilityDiff, CapabilityState } from "@ando/core";

import { useCapabilityManifest } from "./useCapabilityManifest.js";

export type CapabilityUiState = CapabilityState | "loading";

export interface UseCapabilityResult {
  readonly diff: CapabilityDiff | null;
  readonly state: CapabilityUiState;
  readonly refetch: () => Promise<unknown>;
}

export function useCapability(name: string): UseCapabilityResult {
  const query = useCapabilityManifest();
  const refetch = () => query.refetch();

  if (query.isLoading && !query.data) {
    return { diff: null, state: "loading", refetch };
  }

  // ``data`` AND ``data.capabilities`` can each be undefined (BE may
  // return ``{}`` while the manifest is being rehydrated). Without the
  // second optional chain the bracket access throws
  // ``undefined is not an object`` (June 3, 2026 — Tauri shell crash).
  const diff = query.data?.capabilities?.[name] ?? null;
  if (!diff) {
    // Manifest is loaded but the BE doesn't expose this capability for the
    // current user — treat as ``satisfied`` so callers render nothing instead
    // of nagging about a slot they don't have access to anyway.
    return { diff: null, state: "satisfied", refetch };
  }
  return { diff, state: diff.state, refetch };
}

/**
 * Boolean fast-path — ``true`` iff the capability is currently satisfied.
 * Defaults to ``false`` while loading so feature gates fail closed.
 */
export function useHasCapability(name: string): boolean {
  const { state } = useCapability(name);
  return state === "satisfied";
}
